import { sanitizeHtml } from "utils/sanitizeHtml";
import { useData } from "data/data-provider";
import React, { FC, useState } from "react";
import { sectionPaddingY } from "utils/sectionSpacing";

export interface SectionClientSayProps {
  className?: string;
}

// Custom Star component
const CustomStar: React.FC<{ filled?: boolean }> = ({ filled = true }) => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-5 h-5"
    >
      <path
        d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
        fill={filled ? "#FFC107" : "#E5E7EB"}
        stroke={filled ? "#E8A902" : "#D1D5DB"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Check-circle icon used next to the author name as a subtle "verified"
// signal — adds credibility without needing a role field on the data
// model.
const VerifiedIcon: React.FC<{ className?: string }> = ({ className = "" }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
  >
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm3.707-9.293a1 1 0 0 0-1.414-1.414L9 10.586 7.707 9.293a1 1 0 0 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4z"
      clipRule="evenodd"
    />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────
// Card variant — used by the GRID layout.
//
// Structured card: padded, drop-shadow, ring border, avatar + name top-
// left, 5-star row, quote below. Multiple visible at once.
// ─────────────────────────────────────────────────────────────────────
const TestimonialCard: FC<{ item: any }> = ({ item }) => (
  <div className="bg-card-color rounded-2xl shadow-lg overflow-hidden border border-secondary-100 hover:shadow-xl transition-shadow">
    <div className="p-6">
      <div className="flex items-center">
        {item?.image_url && (
          <img
            src={item?.image_url}
            alt=""
            className="w-12 h-12 shrink-0 rounded-full object-cover"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        )}
        <div className="ml-3">
          <h4 className="font-medium">
            <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(item?.heading) }} />
          </h4>
        </div>
      </div>
      <div className="my-2 border-b"></div>
      <div className="flex items-center mb-4">
        {[...Array(5)].map((_, i) => (
          <CustomStar key={i} filled={true} />
        ))}
        <span className="ml-2 text-neutral-500">(5)</span>
      </div>
      <p className="text-secondary-text-color mb-6 line-clamp-6 ">
        <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(item?.description) }} />
      </p>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────
// Featured-quote variant — used by the CAROUSEL layout.
//
// Editorial style modeled on Stripe / Apple / Linear customer-quote
// hero sections: NO card chrome (rounded panel backdrop, not a card),
// stylized SVG quote glyph, italic light-weight quote text, centered
// avatar + verified-name row at the bottom. One testimonial visible at
// a time; nav handled by parent (prev/next arrows + clickable dots).
// ─────────────────────────────────────────────────────────────────────
const FeaturedTestimonial: FC<{ item: any }> = ({ item }) => (
  <div className="shrink-0 w-full">
    <div className="w-full flex flex-col items-center text-center px-2 sm:px-4">
      {/* No quotation-mark ornament — Stripe / Apple / Vercel style. The
          italic typography on the white panel + 5-star row already read
          unambiguously as a testimonial; an oversized quote glyph just
          competed for attention.

          The OUTER column now goes full panel width so the testimonial
          sits centered within the entire panel instead of as a 768px
          island. Only the quote <p> below carries its own max-width for
          readability — line lengths past ~70ch get hard to read. */}

      {/* Stars */}
      <div className="flex items-center justify-center gap-0.5 mb-6">
        {[...Array(5)].map((_, i) => (
          <CustomStar key={i} filled={true} />
        ))}
      </div>

      {/* The quote — large, italic, generous leading. max-w-3xl keeps
          line lengths under ~70ch so the text stays readable even though
          the surrounding panel is full container width. line-clamp keeps
          very long testimonials from blowing up the height.
          `px-2`: line-clamp sets `overflow:hidden` and the centered element
          shrinks to the text width, which clipped the right overhang of the
          trailing *italic* glyph (e.g. "cyt2" looked cut off — 1NOW-1247).
          The small symmetric padding gives the slanted glyph room; the text
          stays centered. */}
      <p className="max-w-3xl px-2 text-lg sm:text-xl md:text-2xl italic font-light leading-relaxed text-text-color mb-8 line-clamp-6">
        <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(item?.description) }} />
      </p>

      {/* Author — avatar + name + small verified check. Inline at the
          bottom so the quote stays the visual centerpiece. */}
      <div className="flex items-center justify-center gap-3">
        {item?.image_url && (
          <img
            src={item.image_url}
            alt=""
            className="w-12 h-12 shrink-0 rounded-full object-cover ring-2 ring-primary-200/50"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        )}
        <div className="text-left">
          <div className="flex items-center gap-1.5">
            <h4 className="font-semibold text-text-color text-base">
              <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(item?.heading) }} />
            </h4>
            <VerifiedIcon className="h-4 w-4 text-primary-200" />
          </div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-secondary-text-color mt-0.5">
            Verified Renter
          </p>
        </div>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────
// Square chevron button — reused for prev/next nav on the carousel.
// ─────────────────────────────────────────────────────────────────────
const NavButton: FC<{
  direction: "prev" | "next";
  onClick: () => void;
  disabled?: boolean;
}> = ({ direction, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={direction === "prev" ? "Previous testimonial" : "Next testimonial"}
    className={`inline-flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-full bg-card-color text-text-color ring-1 ring-secondary-100 shadow-md transition-all
      ${disabled
        ? "opacity-30 cursor-not-allowed"
        : "hover:bg-primary-200 hover:text-on-primary hover:ring-primary-200 hover:shadow-lg active:scale-95"
      }`}
  >
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
    >
      {direction === "prev" ? (
        <polyline points="15 18 9 12 15 6" />
      ) : (
        <polyline points="9 18 15 12 9 6" />
      )}
    </svg>
  </button>
);

const SectionTestimonials: FC<SectionClientSayProps> = ({ className = "" }) => {
  const { testimonials } = useData();
  // 1NOW layout variant chosen in the FMS (Section.layout_variant):
  //   "grid"     → 4-column responsive grid (current visual / default)
  //   "carousel" → editorial featured-quote slider, one at a time
  // Default "grid" preserves the previous render for tenants who haven't
  // picked a variant.
  const layoutVariant: "grid" | "carousel" =
    testimonials?.layout_variant === "carousel" ? "carousel" : "grid";
  const items: any[] = testimonials?.keypoints ?? [];

  // Carousel state — JS-driven `translateX` instead of native overflow-x
  // so the browser horizontal scrollbar never leaks through (the
  // `scrollbar-hide` Tailwind utility isn't loaded in this project, so
  // relying on it produced a visible bar on the old version).
  const [currentIndex, setCurrentIndex] = useState(0);
  const goTo = (i: number) =>
    setCurrentIndex(Math.max(0, Math.min(items.length - 1, i)));

  return (
    <div
      // Tinted section panel (bg-secondary-50 = the theme's component bg) so
      // testimonials read as their own band instead of floating on the page
      // bg. Rounded + horizontal padding makes it a contained card; the
      // vertical padding is tightened (py-16 → py-10/12) for a more compact
      // section per design feedback.
      className={`nc-SectionClientSay relative ${className} bg-secondary-50 rounded-3xl ${sectionPaddingY(
        testimonials?.vertical_spacing,
        "py-10 sm:py-12"
      )} px-4 sm:px-8`}
      data-nc-id="SectionClientSay"
    >
      <div>
        <div className="text-center mb-8 sm:mb-10">
          <h2 className="text-4xl font-bold text-text-color mb-4">
            <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(testimonials?.heading) }} />
          </h2>
        </div>

        {layoutVariant === "grid" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {items.map((item: any) => (
              <TestimonialCard key={item.id} item={item} />
            ))}
          </div>
        )}

        {/* Carousel layout — featured-quote slider. Soft tinted panel
            anchors the quote so it doesn't float in white space; JS-
            driven translateX advances slides so no browser scrollbar
            leaks through. Arrows + clickable dots for navigation. */}
        {layoutVariant === "carousel" && items.length > 0 && (
          <div className="relative w-full">
            {/* Backdrop panel — white with soft shadow so the testimonial
                pops off any section background (the prior tinted backdrop
                blended into themes that use a cream section bg, leaving
                the quote floating with no visible container). */}
            <div className="relative overflow-hidden rounded-3xl bg-card-color ring-1 ring-secondary-100 shadow-xl shadow-secondary-100/30 py-12 sm:py-16 px-4 sm:px-12">
              {/* Decorative blob in the top-right corner — subtle brand
                  accent that gives the white panel some life. */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-12 -right-12 h-48 w-48 rounded-full opacity-30 blur-3xl"
                style={{
                  background:
                    "radial-gradient(circle at center, var(--c-primary-200, #2dd4bf) 0%, transparent 65%)",
                }}
              />

              {/* Slide track. transform:translateX shifts whichever slide
                  is current into view. transition gives a smooth glide. */}
              <div className="overflow-hidden">
                <div
                  className="flex transition-transform duration-500 ease-out"
                  style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                >
                  {items.map((item: any) => (
                    <FeaturedTestimonial key={item.id} item={item} />
                  ))}
                </div>
              </div>
            </div>

            {/* Nav controls — arrows + clickable dots. Only show when
                there's more than one testimonial; otherwise the controls
                are noise. */}
            {items.length > 1 && (
              <div className="mt-6 flex items-center justify-center gap-4">
                <NavButton
                  direction="prev"
                  onClick={() => goTo(currentIndex - 1)}
                  disabled={currentIndex === 0}
                />

                {/* Dot pagination — each dot is clickable to jump directly
                    to that slide. Active dot is wider + brand-colored. */}
                <div className="flex items-center gap-2">
                  {items.map((item: any, i: number) => (
                    <button
                      key={item.id || i}
                      type="button"
                      onClick={() => goTo(i)}
                      aria-label={`Go to testimonial ${i + 1}`}
                      className={`h-2 rounded-full transition-all ${
                        i === currentIndex
                          ? "w-7 bg-primary-200"
                          : "w-2 bg-secondary-text-color/30 hover:bg-secondary-text-color/50"
                      }`}
                    />
                  ))}
                </div>

                <NavButton
                  direction="next"
                  onClick={() => goTo(currentIndex + 1)}
                  disabled={currentIndex === items.length - 1}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SectionTestimonials;
